import React, { useEffect, useMemo, useState } from 'react';
import Card from '../components/Card';
import { inputStyle } from '../components/FormField';
import * as analyticsApi from '../services/analyticsApi';

function ChangeBadge({ pct }) {
  if (pct === null) return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
  const color = pct > 0 ? 'var(--color-success)' : pct < 0 ? 'var(--color-danger)' : 'var(--color-text-muted)';
  return <span className="data-figure" style={{ color, fontWeight: 600 }}>{pct > 0 ? '+' : ''}{pct}%</span>;
}

function ClassDot({ value, colors }) {
  if (!value) return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 20, height: 20, borderRadius: '50%', background: colors[value] || 'var(--color-text-muted)',
      color: '#fff', fontSize: 10, fontWeight: 700,
    }}>
      {value}
    </span>
  );
}
const ABC_COLORS = { A: 'var(--color-danger)', B: 'var(--color-warning)', C: 'var(--color-text-muted)' };
const FSN_COLORS = { F: 'var(--color-success)', S: 'var(--color-warning)', N: 'var(--color-text-muted)' };

const COLUMNS = [
  { key: 'designName', label: 'Design', sortable: true, type: 'string' },
  { key: 'categoryName', label: 'Category', sortable: false },
  { key: 'currentStock', label: 'Stock', sortable: true, type: 'number' },
  { key: 'pendingOrderQty', label: 'Pending', sortable: true, type: 'number' },
  { key: 'dispatchOnTimePct', label: 'On-Time%', sortable: true, type: 'number' },
  { key: 'plannedProductionQty', label: 'Planned Prod.', sortable: true, type: 'number' },
  { key: 'abcClass', label: 'ABC', sortable: false },
  { key: 'fsnClass', label: 'FSN', sortable: false },
  { key: 'openRecommendations', label: 'Open Recs', sortable: true, type: 'number' },
];
const SHOW_OPTIONS = [10, 25, 50, 'All'];

function SortIcon({ active, dir }) {
  if (!active) return <span style={{ opacity: 0.3 }}>⇅</span>;
  return <span>{dir === 'asc' ? '↑' : '↓'}</span>;
}

export default function AnalyticsPage() {
  const [trend, setTrend] = useState([]);
  const [mis, setMis] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sortKey, setSortKey] = useState('designName');
  const [sortDir, setSortDir] = useState('asc');
  const [abcFilter, setAbcFilter] = useState('');
  const [fsnFilter, setFsnFilter] = useState('');
  const [showCount, setShowCount] = useState('All');

  useEffect(() => {
    Promise.all([
      analyticsApi.getOverviewTrend(12),
      analyticsApi.getMisSummary(),
      analyticsApi.getPeriodComparison(),
    ])
      .then(([t, m, c]) => {
        setTrend(t);
        setMis(m);
        setComparison(c);
      })
      .catch((err) => setError(err.message || 'Could not load analytics.'))
      .finally(() => setLoading(false));
  }, []);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const abcOptions = useMemo(() => Array.from(new Set(mis.map((r) => r.abcClass).filter(Boolean))).sort(), [mis]);
  const fsnOptions = useMemo(() => Array.from(new Set(mis.map((r) => r.fsnClass).filter(Boolean))).sort(), [mis]);

  const processedRows = useMemo(() => {
    let rows = mis;
    if (abcFilter) rows = rows.filter((r) => r.abcClass === abcFilter);
    if (fsnFilter) rows = rows.filter((r) => r.fsnClass === fsnFilter);

    const col = COLUMNS.find((c) => c.key === sortKey);
    rows = [...rows].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      let cmp;
      if (col?.type === 'number') cmp = Number(av) - Number(bv);
      else cmp = String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return showCount === 'All' ? rows : rows.slice(0, showCount);
  }, [mis, abcFilter, fsnFilter, sortKey, sortDir, showCount]);

  const totals = useMemo(() => ({
    currentStock: processedRows.reduce((s, r) => s + (r.currentStock || 0), 0),
    pendingOrderQty: processedRows.reduce((s, r) => s + (r.pendingOrderQty || 0), 0),
    plannedProductionQty: processedRows.reduce((s, r) => s + (r.plannedProductionQty || 0), 0),
    openRecommendations: processedRows.reduce((s, r) => s + (r.openRecommendations || 0), 0),
  }), [processedRows]);

  const maxTrendQty = Math.max(1, ...trend.flatMap((t) => [t.orderedQty, t.dispatchedQty, t.producedQty]));

  return (
    <div>
      <h1>Analytics &amp; MIS Center</h1>
      <p style={{ color: 'var(--color-text-secondary)' }}>
        Cross-cutting views pulling together what Stock, Order, Dispatch, Production, and AI
        Classification already compute — one place to compare everything, not six.
      </p>

      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {loading ? (
        <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
      ) : (
        <>
          <Card title="Supply vs Demand Trend (12 weeks)">
            <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 140, overflowX: 'auto' }}>
              {trend.map((t) => (
                <div key={t.weekStart} title={`${t.weekStart}: ordered ${t.orderedQty}, dispatched ${t.dispatchedQty}, produced ${t.producedQty}`} style={{ display: 'flex', gap: 2, alignItems: 'flex-end', minWidth: 40 }}>
                  <div style={{ width: 10, height: `${(t.orderedQty / maxTrendQty) * 110}px`, background: 'var(--color-accent)' }} />
                  <div style={{ width: 10, height: `${(t.dispatchedQty / maxTrendQty) * 110}px`, background: 'var(--color-success)' }} />
                  <div style={{ width: 10, height: `${(t.producedQty / maxTrendQty) * 110}px`, background: 'var(--color-info)' }} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 'var(--space-3)', fontSize: 'var(--font-size-xs)' }}>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--color-accent)', marginRight: 4 }} />Ordered</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--color-success)', marginRight: 4 }} />Dispatched</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--color-info)', marginRight: 4 }} />Produced</span>
            </div>
          </Card>

          {comparison && (
            <Card title="Period Comparison (last 30 days vs. prior 30 days)">
              <div className="kpi-row" style={{ display: 'flex', gap: 'var(--space-6)' }}>
                {[
                  { label: 'Ordered', curr: comparison.current.ordered.qty, prev: comparison.previous.ordered.qty, pct: comparison.changePct.ordered },
                  { label: 'Dispatched', curr: comparison.current.dispatched.qty, prev: comparison.previous.dispatched.qty, pct: comparison.changePct.dispatched },
                  { label: 'Produced', curr: comparison.current.produced.qty, prev: comparison.previous.produced.qty, pct: comparison.changePct.produced },
                ].map((m) => (
                  <div key={m.label}>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{m.label}</div>
                    <div className="data-figure" style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>{m.curr}</div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      vs <span className="data-figure">{m.prev}</span> · <ChangeBadge pct={m.pct} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card title="MIS Summary — one row per design">
            <div className="kpi-row" style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-end', marginBottom: 'var(--space-4)', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', gap: 4 }}>
                ABC Class
                <select value={abcFilter} onChange={(e) => setAbcFilter(e.target.value)} style={inputStyle}>
                  <option value="">All</option>
                  {abcOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', gap: 4 }}>
                FSN Class
                <select value={fsnFilter} onChange={(e) => setFsnFilter(e.target.value)} style={inputStyle}>
                  <option value="">All</option>
                  {fsnOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', gap: 4 }}>
                Show
                <select value={showCount} onChange={(e) => setShowCount(e.target.value === 'All' ? 'All' : Number(e.target.value))} style={inputStyle}>
                  {SHOW_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>
            </div>

            <div className="table-scroll">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>
                  {COLUMNS.map((c) => (
                    <th key={c.key} style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      {c.sortable ? (
                        <button
                          type="button"
                          onClick={() => handleSort(c.key)}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 600, padding: 0, fontFamily: 'inherit' }}
                        >
                          {c.label} <SortIcon active={sortKey === c.key} dir={sortDir} />
                        </button>
                      ) : c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {processedRows.map((row) => (
                  <tr key={row.design_id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{row.designName}</td>
                    <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{row.categoryName || '—'}</td>
                    <td
                      className="data-figure"
                      style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', color: row.stockFlags?.negative ? 'var(--color-danger)' : row.stockFlags?.low ? 'var(--color-warning)' : 'var(--color-text-primary)' }}
                    >
                      {row.currentStock ?? '—'}
                    </td>
                    <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{row.pendingOrderQty}</td>
                    <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{row.dispatchOnTimePct ?? '—'}</td>
                    <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{row.plannedProductionQty}</td>
                    <td style={{ padding: '8px 4px' }}><ClassDot value={row.abcClass} colors={ABC_COLORS} /></td>
                    <td style={{ padding: '8px 4px' }}><ClassDot value={row.fsnClass} colors={FSN_COLORS} /></td>
                    <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: row.openRecommendations > 0 ? 600 : 400, color: row.openRecommendations > 0 ? 'var(--color-accent)' : 'var(--color-text-muted)' }}>
                      {row.openRecommendations}
                    </td>
                  </tr>
                ))}
                {processedRows.length === 0 && (
                  <tr><td colSpan={9} style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)' }}>No designs match this filter.</td></tr>
                )}
                {processedRows.length > 0 && (
                  <tr style={{ borderTop: '2px solid var(--color-border)', fontWeight: 700 }}>
                    <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>Total</td>
                    <td />
                    <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{totals.currentStock}</td>
                    <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{totals.pendingOrderQty}</td>
                    <td />
                    <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{totals.plannedProductionQty}</td>
                    <td />
                    <td />
                    <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{totals.openRecommendations}</td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
